import { Navigate, useNavigate } from 'react-router-dom'

import { loginPin } from '@/features/auth/api/pinLogin'
import { formatPinLoginError, isDeviceRevokedError, PIN_LOGIN_COPY } from '@/features/auth/pinLoginErrors'
import { SwitchDeviceFooter } from '@/features/auth/SwitchDeviceFooter'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { PinEntryCard } from '@/shared/components/PinEntryCard'
import { clearDeviceUid } from '@/shared/auth/deviceUid'
import { clearDelmanDevice, getDelmanDevice, hasDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { clearDelmanSession, hasDelmanSession, setDelmanSession } from '@/shared/auth/delmanSessionStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

export function DelmanLoginPage() {
  const navigate = useNavigate()
  const hydrated = useClientHydrated()

  if (hydrated && !hasDelmanDevice()) {
    return <Navigate to="/delman/activate" replace />
  }
  if (hydrated && hasDelmanSession()) {
    return <Navigate to="/delman/trip" replace />
  }

  const device = hydrated ? getDelmanDevice() : null

  function resetLocalDevice() {
    clearDelmanSession()
    clearDelmanDevice()
    clearDeviceUid()
  }

  async function handleSubmit(pin: string) {
    if (!device) throw new Error(PIN_LOGIN_COPY.loginFailed)

    try {
      const res = await loginPin({ device_id: device.deviceId, pin })
      if (res.user.role_code !== 'DELMAN_WAREHOUSE') {
        throw new Error(delmanTerms.login.roleNotAllowed)
      }
      setDelmanSession({
        accessToken: res.access_token,
        userId: res.user.id,
        userName: res.user.name,
        roleCode: 'DELMAN_WAREHOUSE',
        brandId: res.brand_id,
        brandCode: res.brand_code,
        loggedInAt: new Date().toISOString(),
      })
      navigate('/delman/trip', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : PIN_LOGIN_COPY.loginFailed
      if (isDeviceRevokedError(message)) {
        resetLocalDevice()
        navigate('/delman/activate', { replace: true })
        return
      }
      throw new Error(formatPinLoginError(message))
    }
  }

  return (
    <PinEntryCard
      icon="local_shipping"
      deviceLabel={device ? `${device.delmanName} · ${device.deviceName}` : undefined}
      title={delmanTerms.login.title}
      subtitle={delmanTerms.login.subtitle}
      helper={delmanTerms.login.helper}
      submitLabel={delmanTerms.login.submit}
      submittingLabel={delmanTerms.login.submitting}
      onSubmit={handleSubmit}
      footer={<SwitchDeviceFooter activateHref="/delman/activate" onReset={resetLocalDevice} />}
    />
  )
}
